import React, { useState, Dispatch, SetStateAction } from 'react';

import { TextField, MenuItem, Box, InputAdornment } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Button } from '@apigrowfinance/pbolt-button';
import { PDialogAction, PDialogContent } from '@apigrowfinance/pbolt-dialog';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import {
    RiAccountCircleLine,
    RiArrowUpDownLine,
    RiCalendarLine,
} from 'react-icons/ri';
import { FormattedMessage } from 'react-intl';

import { CustomersItem } from '@clients/customersClient';
import { TransactionsType } from '@clients/transactionsClient';
import enumTranslations from '@messages/enumTranslations';
import transactionTypes from '@messages/transactionTypes';
import {
    FilterOptions,
    TransactionFilters,
} from '@pages/transactions/Transactions';
import getEnumTranslation from '@utils/getEnumTranslation';

export type TransactionsFilterFormProps = {
    filterOptions: FilterOptions;
    setTransactionFilters: Dispatch<SetStateAction<TransactionFilters>>;
    transactionFilters: TransactionFilters;
    setFilterTransactionsOpen: Dispatch<SetStateAction<boolean>>;
    refetch?: () => void;
};

const TransactionsFilterForm = ({
    filterOptions,
    setTransactionFilters,
    transactionFilters,
    setFilterTransactionsOpen,
    refetch,
}: TransactionsFilterFormProps) => {
    const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
    const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

    const {
        handleSubmit,
        control,
        formState: { isDirty },
        reset,
    } = useForm({
        defaultValues: {
            customerId: transactionFilters?.customerId,
            eventDateStart: transactionFilters?.eventDateStart,
            eventDateEnd: transactionFilters?.eventDateEnd,
            credit: transactionFilters?.credit,
            order: transactionFilters?.order,
            transactionTypeId: transactionFilters?.transactionTypeId,
            authorizationType: transactionFilters?.authorizationType,
            processingCode: transactionFilters?.processingCode,
            authorizationId: transactionFilters?.authorizationId,
            authorizationTrackingId:
                transactionFilters?.authorizationTrackingId,
            statementPost: transactionFilters?.statementPost,
        },
    });

    const { theme } = useTheme();

    const onSubmit = (data: TransactionFilters) => {
        if (data?.eventDateStart) {
            data.eventDateStart = dayjs(data?.eventDateStart).toISOString();
        }
        if (data?.eventDateEnd) {
            data.eventDateEnd = dayjs(data?.eventDateEnd).toISOString();
        }
        setTransactionFilters(data);
        setFilterTransactionsOpen(false);
        refetch();
    };

    const sortOptions = [
        {
            label: 'Oldest to newest',
            value: 'asc',
        },
        {
            label: 'Newest to oldest',
            value: 'desc',
        },
    ];

    const typeOptions = [
        {
            label: 'Network',
            value: 'NETWORK',
        },
        {
            label: 'Platform',
            value: 'PLATFORM',
        },
    ];

    const creditOptions = [
        {
            label: 'Credit',
            value: 'true',
        },
        {
            label: 'Debit',
            value: 'false',
        },
    ];

    const statementOptions = [
        {
            label: 'True',
            value: 'true',
        },
        {
            label: 'False',
            value: 'false',
        },
    ];

    return (
        <form
            data-testid="transactions-filter-form"
            style={{ width: '100%' }}
            onSubmit={(e) => {
                e.preventDefault();
            }}>
            <PDialogContent>
                <Controller
                    name="customerId"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <RiAccountCircleLine
                                            size={theme.spacing(3)}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                            select
                            label={
                                <FormattedMessage
                                    id="TransactionsFilterForm.customer"
                                    defaultMessage="Customer"
                                />
                            }>
                            {filterOptions?.customerOptions?.map(
                                ({ customer }: CustomersItem) => (
                                    <MenuItem
                                        key={customer?.id}
                                        value={customer?.id}>
                                        {customer?.printed_name}
                                    </MenuItem>
                                ),
                            )}
                        </TextField>
                    )}
                />
                <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-evenly">
                    <Controller
                        name="eventDateStart"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                open={startDatePickerOpen}
                                disableFuture
                                label={
                                    <FormattedMessage
                                        id="TransactionsFilterForm.startDate"
                                        defaultMessage="Start Date"
                                    />
                                }
                                {...field}
                                onClose={() => setStartDatePickerOpen(false)}
                                renderInput={(
                                    params: Record<string, unknown>,
                                ) => (
                                    <TextField
                                        onClick={() =>
                                            setStartDatePickerOpen(true)
                                        }
                                        fullWidth
                                        {...params}
                                        sx={{ mr: 1 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <RiCalendarLine />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        )}
                    />
                    <Controller
                        name="eventDateEnd"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                open={endDatePickerOpen}
                                disableFuture
                                label={
                                    <FormattedMessage
                                        id="TransactionsFilterForm.endDate"
                                        defaultMessage="End Date"
                                    />
                                }
                                {...field}
                                onClose={() => setEndDatePickerOpen(false)}
                                renderInput={(
                                    params: Record<string, unknown>,
                                ) => (
                                    <TextField
                                        onClick={() =>
                                            setEndDatePickerOpen(true)
                                        }
                                        fullWidth
                                        {...params}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <RiCalendarLine />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        )}
                    />
                </Box>
                <Controller
                    name="order"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            select
                            label={
                                <FormattedMessage
                                    id="TransactionsFilterForm.sort"
                                    defaultMessage="Sort by"
                                />
                            }
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <RiArrowUpDownLine />
                                    </InputAdornment>
                                ),
                            }}>
                            {sortOptions?.map((option) => (
                                <MenuItem
                                    key={option.label}
                                    value={option.value}>
                                    {getEnumTranslation(option.value, {
                                        messages: transactionTypes,
                                    })}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                />
                <Controller
                    name="credit"
                    control={control}
                    render={({ field }) => {
                        return (
                            <TextField
                                {...field}
                                select
                                onChange={(event) => {
                                    field.onChange(
                                        event.target.value === 'true',
                                    );
                                    setTransactionFilters({
                                        ...transactionFilters,
                                        credit: event.target.value === 'true',
                                    });
                                }}
                                label={
                                    <FormattedMessage
                                        id="TransactionsFilterForm.creditDebit"
                                        defaultMessage="Credit/Debit"
                                    />
                                }>
                                {creditOptions?.map((option) => (
                                    <MenuItem
                                        key={option.label}
                                        value={option.value}>
                                        {getEnumTranslation(
                                            option.label.toLocaleLowerCase(),
                                            {
                                                messages: enumTranslations,
                                            },
                                        )}
                                    </MenuItem>
                                ))}
                            </TextField>
                        );
                    }}
                />
                <Controller
                    name="transactionTypeId"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            select
                            label={
                                <FormattedMessage
                                    id="TransactionsFilterForm.transactionTypes"
                                    defaultMessage="Transaction Type"
                                />
                            }>
                            {filterOptions?.transactionTypes?.map(
                                (option: TransactionsType) => (
                                    <MenuItem
                                        key={
                                            option.transaction_type_description
                                        }
                                        value={option.transaction_type_id}>
                                        {getEnumTranslation(
                                            option.transaction_type_id.toString(),
                                            {
                                                messages: transactionTypes,
                                            },
                                        )}
                                    </MenuItem>
                                ),
                            )}
                        </TextField>
                    )}
                />
                <Controller
                    name="authorizationType"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            select
                            label={
                                <FormattedMessage
                                    id="CardDetailsForm.authType"
                                    defaultMessage="Authorisation Type"
                                />
                            }>
                            {typeOptions?.map((option) => (
                                <MenuItem
                                    key={option.label}
                                    value={option.value}>
                                    {getEnumTranslation(option.value, {
                                        messages: transactionTypes,
                                    })}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                />
                <Controller
                    name="processingCode"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={
                                <FormattedMessage
                                    id="TransactionsFilterForm.processingCode"
                                    defaultMessage="Processing Code"
                                />
                            }
                        />
                    )}
                />
                <Controller
                    name="authorizationId"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            type="number"
                            {...field}
                            label={
                                <FormattedMessage
                                    id="TransactionsFilterForm.authorizationId"
                                    defaultMessage="Authorisation ID"
                                />
                            }
                        />
                    )}
                />
                <Controller
                    name="authorizationTrackingId"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={
                                <FormattedMessage
                                    id="TransactionsFilterForm.authorizationTrackingId"
                                    defaultMessage="Authorisation Tracking ID"
                                />
                            }
                        />
                    )}
                />
                <Controller
                    name="statementPost"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            select
                            onChange={(event) => {
                                field.onChange(event.target.value === 'true');
                                setTransactionFilters({
                                    ...transactionFilters,
                                    credit: event.target.value === 'true',
                                });
                            }}
                            label={
                                <FormattedMessage
                                    id="CardDetailsForm.statementPost"
                                    defaultMessage="Statement Post"
                                />
                            }>
                            {statementOptions?.map((option) => (
                                <MenuItem
                                    key={option.label}
                                    value={option.value}>
                                    {getEnumTranslation(option.value, {
                                        messages: enumTranslations,
                                    })}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                />
            </PDialogContent>
            <PDialogAction>
                <Button
                    onClick={() => {
                        setTransactionFilters({});
                        setFilterTransactionsOpen(false);
                        reset({
                            customerId: undefined,
                            eventDateStart: undefined,
                            eventDateEnd: undefined,
                            credit: undefined,
                            order: undefined,
                            transactionTypeId: undefined,
                            authorizationType: undefined,
                            processingCode: undefined,
                            authorizationId: undefined,
                            authorizationTrackingId: undefined,
                            statementPost: undefined,
                        });
                    }}>
                    {getEnumTranslation('reset')}
                </Button>
                <Button
                    disabled={!isDirty}
                    onClick={handleSubmit(onSubmit)}
                    type="submit"
                    variant="contained">
                    {getEnumTranslation('save')}
                </Button>
            </PDialogAction>
        </form>
    );
};

export default TransactionsFilterForm;
